import React from 'react';
import { Box, Typography, Paper, Grid, Divider, useTheme, useMediaQuery } from '@material-ui/core';
import { ReportCardComponentProps, SchemaComponentRegistry } from '../schema-types';
import { filterDOMProps } from '../utils/prop-normalizer';
import { processTextContent } from './text-utils';
import { ReportCardQuestion } from '../../DevelopmentAssessmentReportCard/report-card-question.component';
import { ScoreRange } from '../../DevelopmentAssessmentReportCard/score-range.component';
import { ChartComponent } from './chart-nivo.component';
import { AccordionComponent } from './accordion.component';
import { MediaCallout } from './media-callout.component';
import { GrowthAnalysis } from './growth-analysis.component';
import { SkillComparisonChart } from './skill-comparison-chart.component';
import { ColumnLayout } from './column-layout.component';
import { CloudImage } from './cloud-image.component';
import { TableComponent } from './table.component';
import { ProgressBarComponent } from './progress-bar.component';
import { BalanceScaleComponent } from './balance-scale.component';
import { CalloutBoxComponent } from './callout-box.component';
import { ScoringGuideComponent } from './scoring-guide.component';
import { LabelComponent } from './label.component';
import { LegacyHeaderComponent } from './legacy-header.component';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import SentimentSatisfiedAltIcon from '@material-ui/icons/SentimentSatisfiedAlt';
import logoIcon from '../../../../assets/logo-icon.png';
import logo from '../../../../assets/logo.png';

// Basic Layout Components
const ContainerComponent: React.FC<ReportCardComponentProps> = ({
  children,
  baseData: _baseData,
  playerAssessment: _playerAssessment,
  reportCardLevel: _reportCardLevel,
  ...props
}) => (
  <Box {...filterDOMProps(props)}>{children}</Box>
);

const GridComponent: React.FC<ReportCardComponentProps> = ({
  children,
  baseData: _baseData,
  playerAssessment: _playerAssessment,
  reportCardLevel: _reportCardLevel,
  ...props
}) => (
  <Grid {...filterDOMProps(props)}>{children}</Grid>
);

const PaperComponent: React.FC<ReportCardComponentProps> = ({
  children,
  baseData: _baseData,
  playerAssessment: _playerAssessment,
  reportCardLevel: _reportCardLevel,
  ...props
}) => (
  <Paper {...filterDOMProps(props)}>{children}</Paper>
);

const DividerComponent: React.FC<ReportCardComponentProps> = ({
  baseData: _baseData,
  playerAssessment: _playerAssessment,
  reportCardLevel: _reportCardLevel,
  ...props
}) => (
  <Divider {...filterDOMProps(props)} />
);


// Typography Components with Mobile Responsiveness
const TypographyComponent: React.FC<ReportCardComponentProps> = ({
  children,
  data,
  baseData,
  player,
  playerAssessment,
  reportCardLevel: _reportCardLevel,
  context,
  style = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Handle different data types
  let content;
  if (typeof data === 'string') {
    content = data;
  } else if (data && typeof data === 'object' && data.content) {
    content = data.content;
  } else {
    content = data || children;
  }

  const processedContent = typeof content === 'string' ? processTextContent(content, 0, data, baseData, player, playerAssessment) : content;

  // Create responsive font size mapping
  const getResponsiveFontSize = (variant: string) => {
    const baseSizes: { [key: string]: string } = {
      h1: isMobile ? '1.75rem' : isTablet ? '2.5rem' : '3rem',
      h2: isMobile ? '1.5rem' : isTablet ? '2rem' : '2.5rem',
      h3: isMobile ? '1.25rem' : isTablet ? '1.75rem' : '2rem',
      h4: isMobile ? '1.125rem' : isTablet ? '1.5rem' : '1.75rem',
      h5: isMobile ? '1rem' : isTablet ? '1.25rem' : '1.5rem',
      h6: isMobile ? '0.875rem' : isTablet ? '1.125rem' : '1.25rem',
      body1: isMobile ? '0.875rem' : isTablet ? '1rem' : '1rem',
      body2: isMobile ? '0.75rem' : isTablet ? '0.875rem' : '0.875rem',
      caption: isMobile ? '0.625rem' : isTablet ? '0.75rem' : '0.75rem',
    };
    return baseSizes[variant] || baseSizes.body1;
  };

  // Create responsive style with maintained aspect ratios
  const responsiveStyle = {
    ...style,
    fontSize: props.variant ? getResponsiveFontSize(props.variant) : undefined,
    lineHeight: isMobile ? 1.3 : isTablet ? 1.4 : 1.5,
    marginBottom: style.marginBottom || (isMobile ? '8px' : isTablet ? '12px' : '16px'),
    marginTop: style.marginTop || (isMobile ? '4px' : isTablet ? '6px' : '8px'),
    // Maintain proportional spacing
    paddingLeft: style.paddingLeft || (isMobile ? '4px' : isTablet ? '6px' : '8px'),
    paddingRight: style.paddingRight || (isMobile ? '4px' : isTablet ? '6px' : '8px'),
  };

  return (
    <Typography
      {...filterDOMProps(props)}
      style={responsiveStyle}
    >
      {processedContent}
    </Typography>
  );
}

const HeaderComponent: React.FC<ReportCardComponentProps> = ({
  data,
  playerAssessment,
  player,
  organizationLogo,
  organizationLogoHeight,
  context,
  reportCardLevel: _reportCardLevel,
  baseData: _baseData,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const playerName = data?.playerName || `${player?.firstName} ${player?.lastName}`;

  // Determine logo size: data > organizationLogoHeight prop > responsive defaults
  const defaultLogoSize = isMobile ? '35px' : isTablet ? '42px' : '50px';
  const logoSize = data?.organizationLogoHeight || organizationLogoHeight || defaultLogoSize;
  
  const headerLogo = React.useMemo(() => {
    const logoStyle = { width: logoSize, height: 'auto' };
    
    if (organizationLogo) {
      // If organizationLogo is a React node, use it directly
      if (typeof organizationLogo === 'string') {
        return <img src={organizationLogo} alt="Organization Logo" style={logoStyle} />;
      } else {
        return organizationLogo;
      }
    } else if (playerAssessment?.assessment?.company?.logo) {
      return <img src={playerAssessment.assessment.company.logo} alt="Organization Logo" style={logoStyle} />;
    } else {
      return <img src={logoIcon} alt="Max Analytics Icon" style={logoStyle} />;
    }
  }, [organizationLogo, playerAssessment?.assessment?.company?.logo, logoSize]);

  // Responsive text sizing and spacing
  const headerVariant = isMobile ? 'h6' : isTablet ? 'h5' : 'h5';
  const marginBottom = isMobile ? 1 : isTablet ? 1.5 : 2;
  const padding = isMobile ? '8px' : isTablet ? '12px' : '16px';

  return (
    <Box
      display="flex"
      justifyContent="space-around"
      alignItems="center"
      mb={marginBottom}
      p={padding}
      flexWrap={isMobile ? 'wrap' : 'nowrap'}
      {...filterDOMProps(props)}
    >
      {/* Left logo - hidden on mobile for space */}
      {!isMobile && headerLogo}

      {/* Center content */}
      <Box textAlign="center" flex={1} mx={isMobile ? 0 : 2}>
        {/* Show logo on mobile above text */}
        {isMobile && (
          <Box mb={1} display="flex" justifyContent="center">
            {headerLogo}
          </Box>
        )}
        <Typography
          align="center"
          variant={headerVariant}
          style={{
            fontWeight: 700,
            fontSize: isMobile ? '1rem' : isTablet ? '1.25rem' : '1.5rem',
            lineHeight: isMobile ? 1.3 : 1.4
          }}
        >
          {playerName}'s{isMobile ? ' ' : <br />}
          Development Assessment
        </Typography>
      </Box>

      {/* Right logo - hidden on mobile */}
      {!isMobile && headerLogo}
    </Box>
  );
};

const QuestionComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <ReportCardQuestion question={data} {...props} />
);

const ScoreRangeComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <ScoreRange
    scoreRange={data?.scoreRange || data}
    title={data?.title || props.title}
    {...props}
  />
);

// Specialized Report Card Components
const SummaryComponent: React.FC<ReportCardComponentProps> = ({
  data,
  baseData,
  player,
  playerAssessment,
  showQuestion = false,
  context,
  reportCardLevel: _reportCardLevel,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const playerFirstName = player?.firstName || 'Player';
  const {
    title = 'Overall',
    scoreRange,
    summaryLeft,
    summaryRight
  } = data || {};

  // Mobile-first responsive styling
  const containerStyle = isMobile ? {
    flexDirection: 'column' as const,
    alignItems: 'stretch' as const,
    gap: '12px'
  } : {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: '16px'
  };

  const paperStyle = {
    padding: isMobile ? 8 : isTablet ? 10 : 12,
    borderRadius: 8,
    flex: isMobile ? 'none' : 1,
    minWidth: 0, // Prevents flex overflow
  };

  const scoreRangeStyle = {
    textAlign: 'center' as const,
    flex: isMobile ? 'none' : '0 0 auto',
    margin: isMobile ? '8px 0' : '0 8px'
  };

  return (
    <Box mt={showQuestion ? 2 : 0} {...filterDOMProps(props)}>
      {showQuestion && <ReportCardQuestion question={`How did ${playerFirstName} perform overall?`} />}
      <Box display="flex" style={containerStyle}>
        <Paper elevation={0} style={{ ...paperStyle, background: '#fffbe7' }}>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            style={{
              fontSize: isMobile ? '0.8rem' : '1rem',
              lineHeight: isMobile ? 1.3 : 1.5,
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {processTextContent(summaryLeft || '', 0, data, baseData, player, playerAssessment)}
          </Typography>
        </Paper>

        <Box style={scoreRangeStyle}>
          <ScoreRange scoreRange={scoreRange} title={title.toUpperCase()} />
        </Box>

        <Paper elevation={0} style={{
          ...paperStyle,
          background: '#f6fff7',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '8px'
        }}>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            style={{
              flex: 1,
              fontSize: isMobile ? '0.8rem' : '1rem',
              lineHeight: isMobile ? 1.3 : 1.5,
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {processTextContent(summaryRight || '', 0, data, baseData, player, playerAssessment)}
          </Typography>
          <SentimentSatisfiedAltIcon
            style={{
              color: '#6b8e23',
              fontSize: isMobile ? '1.2rem' : '1.5rem',
              alignSelf: isMobile ? 'center' : 'auto'
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

const ScoreListComponent: React.FC<ReportCardComponentProps> = ({
  data,
  baseData,
  player,
  playerAssessment,
  showQuestion = false,
  context,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const playerFirstName = player?.firstName || 'Player';
  const { scores = [] } = data || {};

  // Responsive grid sizing: mobile stacks vertically, tablet/desktop shows columns
  const getGridSize = () => {
    if (isMobile) return 12; // Full width on mobile (stacked)
    if (isTablet) return 6;  // 2 columns on tablet
    return 4; // 3 columns on desktop
  };

  const gridSpacing = isMobile ? 1 : isTablet ? 2 : 2;
  const paperPadding = isMobile ? 8 : isTablet ? 12 : 16;

  return (
    <Box mt={showQuestion ? 2 : 0} {...filterDOMProps(props)}>
      {showQuestion && <ReportCardQuestion question={`What is ${playerFirstName}'s biggest strength?`} />}
      <Grid container spacing={gridSpacing} alignItems="stretch" style={{ marginTop: isMobile ? 12 : 0 }}>
        {scores.map((score: any, index: number) => (
          <Grid item xs={getGridSize()} key={index}>
            <Paper elevation={1} style={{
              padding: paperPadding,
              background: '#f6faff',
              borderRadius: 8,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box display="flex" alignItems="center" mb={isMobile ? 0.5 : 1} flexWrap="wrap">
                {index === 0 && <EmojiEventsIcon style={{
                  color: '#fbc02d',
                  marginRight: isMobile ? 4 : 8,
                  fontSize: isMobile ? '1rem' : '1.5rem'
                }} />}
                <Typography
                  variant={isMobile ? "body2" : "subtitle1"}
                  style={{
                    fontWeight: 700,
                    color: '#1976d2',
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    lineHeight: isMobile ? 1.2 : 1.4,
                    wordBreak: 'break-word'
                  }}
                >
                  {score.title?.toUpperCase()}
                </Typography>
              </Box>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                style={{
                  fontWeight: 900,
                  fontSize: isMobile ? '1.1rem' : '1.5rem',
                  lineHeight: 1.2
                }}
              >
                {score.scoreRange}
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "subtitle2"}
                style={{
                  fontSize: isMobile ? '0.65rem' : '0.875rem',
                  letterSpacing: '0.02em'
                }}
              >
                SCORE RANGE
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                style={{
                  marginTop: isMobile ? 4 : 8,
                  fontSize: isMobile ? '0.7rem' : '0.875rem',
                  lineHeight: isMobile ? 1.3 : 1.4,
                  flex: 1, // Takes remaining space
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {processTextContent(score.description || '', 0, data, baseData, player, playerAssessment)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const FooterComponent: React.FC<ReportCardComponentProps> = ({
  data,
  player,
  playerAssessment,
  reportCardLevel: _reportCardLevel,
  baseData: _baseData,
  ...props
}) => {
  const playerName = data?.playerName || `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || 'Player';
  const ageGroup = data?.ageGroup || playerAssessment?.ageGroup?.name || 'Age Group';
  const organization = data?.organization || playerAssessment?.assessment?.company?.name || 'Max Analytics';

  return (
    <Box mt={6} {...filterDOMProps(props)}>
      <Divider style={{ margin: '24px 0' }} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {playerName}<br />
            Age Group: {ageGroup}<br />
            {organization}
          </Typography>
        </Box>
        <img src={logo} alt="Max Analytics Logo" style={{ width: '200px' }} />
      </Box>
    </Box>
  );
};

const MediaCalloutComponent: React.FC<ReportCardComponentProps> = ({
  data,
  player,
  maxWidthRestrictions,
  ...props
}) => {
  return (
    <MediaCallout
      {...data}
      maxWidthRestrictions={maxWidthRestrictions}
      {...props}
    />
  );
};

const GrowthAnalysisComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <GrowthAnalysis {...data} {...props} />
);

const SkillComparisonChartComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <SkillComparisonChart {...data} {...props} />
);

const ColumnLayoutComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <ColumnLayout {...data} {...props} />
);

const CloudImageComponent: React.FC<ReportCardComponentProps> = ({ data, maxWidthRestrictions, ...props }) => {
  return (
    <CloudImage {...data} maxWidthRestrictions={maxWidthRestrictions} {...props} />
  );
}

const TableWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <TableComponent data={data} {...props} />
);

const ProgressBarWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <ProgressBarComponent data={data} {...props} />
);

const BalanceScaleWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <BalanceScaleComponent data={data} {...props} />
);

const CalloutBoxWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <CalloutBoxComponent data={data} {...props} />
);

const ScoringGuideWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <ScoringGuideComponent data={data} {...props} />
);

const LabelWrapperComponent: React.FC<ReportCardComponentProps> = ({ data, ...props }) => (
  <LabelComponent data={data} {...props} />
);

// Registry of all available components
export const componentRegistry: SchemaComponentRegistry = {
  // Layout components
  container: ContainerComponent,
  grid: GridComponent,
  paper: PaperComponent,
  divider: DividerComponent,
  accordion: AccordionComponent,

  // Typography components
  typography: TypographyComponent,

  // Specialized report card components
  header: HeaderComponent,
  legacyHeader: LegacyHeaderComponent,
  question: QuestionComponent,
  scoreRange: ScoreRangeComponent,
  summary: SummaryComponent,
  scoreList: ScoreListComponent,
  footer: FooterComponent,
  chart: ChartComponent,
  mediaCallout: MediaCalloutComponent,
  growthAnalysis: GrowthAnalysisComponent,
  skillComparisonChart: SkillComparisonChartComponent,
  columnLayout: ColumnLayoutComponent,
  cloudImage: CloudImageComponent,

  // New components
  table: TableWrapperComponent,
  progressBar: ProgressBarWrapperComponent,
  balanceScale: BalanceScaleWrapperComponent,
  calloutBox: CalloutBoxWrapperComponent,
  scoringGuide: ScoringGuideWrapperComponent,
  label: LabelWrapperComponent,
};
